/**
 * @fileoverview Rate limiting service for abuse protection.
 * 
 * This service implements client-side rate limiting to prevent abuse
 * and ensure fair usage across all users.
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  keyPrefix: string;
}

interface RateLimitState {
  count: number;
  resetTime: number;
}

class RateLimitService {
  private readonly storage = typeof window !== 'undefined' ? window.localStorage : null;
  private readonly configs: Map<string, RateLimitConfig> = new Map();

  constructor() {
    // Configure rate limits for different operations
    this.configs.set('spinner_create', { maxRequests: 10, windowMs: 60000, keyPrefix: 'rl_spinner' });
    this.configs.set('question_report', { maxRequests: 5, windowMs: 300000, keyPrefix: 'rl_report' });
    this.configs.set('contribution_submit', { maxRequests: 3, windowMs: 600000, keyPrefix: 'rl_contribution' });
    this.configs.set('auth_attempt', { maxRequests: 5, windowMs: 300000, keyPrefix: 'rl_auth' });
    this.configs.set('api_request', { maxRequests: 100, windowMs: 60000, keyPrefix: 'rl_api' });
  }

  /**
   * Check if a request is allowed based on rate limits
   */
  isAllowed(operation: string, userId?: string): { allowed: boolean; remaining: number; resetTime: number } {
    const config = this.configs.get(operation);
    if (!config || !this.storage) {
      return { allowed: true, remaining: 999, resetTime: Date.now() };
    }

    const key = `${config.keyPrefix}_${operation}_${userId || 'anonymous'}`;
    const now = Date.now();
    
    try {
      const stored = this.storage.getItem(key);
      let state: RateLimitState;
      
      if (stored) {
        state = JSON.parse(stored);
        if (now > state.resetTime) {
          // Reset window
          state = { count: 0, resetTime: now + config.windowMs };
        }
      } else {
        state = { count: 0, resetTime: now + config.windowMs };
      }

      const remaining = Math.max(0, config.maxRequests - state.count);
      const allowed = state.count < config.maxRequests;

      return { allowed, remaining, resetTime: state.resetTime };
    } catch (error) {
      console.warn('Rate limit check failed:', error);
      return { allowed: true, remaining: 999, resetTime: now };
    }
  }

  /**
   * Record a request attempt
   */
  recordRequest(operation: string, userId?: string): void {
    const config = this.configs.get(operation);
    if (!config || !this.storage) return;

    const key = `${config.keyPrefix}_${operation}_${userId || 'anonymous'}`;
    const now = Date.now();
    
    try {
      const stored = this.storage.getItem(key);
      let state: RateLimitState;
      
      if (stored) {
        state = JSON.parse(stored);
        if (now > state.resetTime) {
          state = { count: 1, resetTime: now + config.windowMs };
        } else {
          state.count++;
        }
      } else {
        state = { count: 1, resetTime: now + config.windowMs };
      }

      this.storage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn('Rate limit record failed:', error);
    }
  }

  /**
   * Get rate limit info for an operation
   */
  getRateLimitInfo(operation: string, userId?: string): RateLimitConfig | null {
    return this.configs.get(operation) || null;
  }

  /**
   * Reset rate limits for a user (admin function)
   */
  resetUserLimits(userId: string): void {
    if (!this.storage) return;

    try {
      const keys = Object.keys(this.storage);
      const userKeys = keys.filter(key => key.includes(userId));
      userKeys.forEach(key => this.storage!.removeItem(key));
    } catch (error) {
      console.warn('Rate limit reset failed:', error);
    }
  }

  /**
   * Check if user is rate limited for multiple operations
   */
  checkMultipleOperations(operations: string[], userId?: string): { allowed: boolean; blockedOperations: string[] } {
    const blockedOperations: string[] = [];
    let allAllowed = true;

    for (const operation of operations) {
      const result = this.isAllowed(operation, userId);
      if (!result.allowed) {
        blockedOperations.push(operation);
        allAllowed = false;
      }
    }

    return { allowed: allAllowed, blockedOperations };
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();
export default rateLimitService; 